import '@types'
import { ref, toValue } from 'vue'
import { useAccountStore } from '@/stores/account.js'

// Enum of accepted code types for the gift card/promo code input
export const CodeInputType = Object.freeze({
  GiftCard: 'gift-card',
  PromoCode: 'promo-code',
  Both: 'both'
})

/** @typedef {typeof CodeInputType[keyof typeof CodeInputType]} CodeInputTypeValue */

/**
 * Composable for handling gift card and promo code activation/application
 * 
 * @param {Object} options
 * @param {ComposableInput<CodeInputTypeValue>} options.accepts - What type of codes to accept
 * @returns {Object} - Methods and state for gift card/promo code handling
 */
export function useGiftCardPromoCode (options = {}) {
  const { accepts: acceptsOption = CodeInputType.Both } = options

  const accountStore = useAccountStore()
  const promoCodeInput = ref('')
  const appliedPromoCode = ref(null)
  const internalErrors = ref([])
  const isApplying = ref(false)

  const isGiftCardCode = (code) => {
    const gcRegex = /^[\w\d]{4}-[\w\d]{4}-[\w\d]{4}-[\w\d]{4}$/
    return gcRegex.test(code)
  }

  /**
   * Apply the current promo code or gift card
   * @returns {Promise<{type: CodeInputType.GiftCard} | {type: CodeInputType.PromoCode, code: string} | null>}
   * @throws {Array} - Throws errors array on failure
   */
  const applyCode = async () => {
    isApplying.value = true
    internalErrors.value = []

    const code = promoCodeInput.value.trim()
    const accepts = toValue(acceptsOption)

    if (!code) {
      isApplying.value = false
      return null
    }

    try {
      const isGiftCard = isGiftCardCode(code)

      // Validate input type against accepted types
      if (isGiftCard && accepts === CodeInputType.PromoCode) {
        internalErrors.value = ['Please enter a promo code.']
        throw internalErrors.value
      }

      if (!isGiftCard && accepts === CodeInputType.GiftCard) {
        internalErrors.value = ['Please enter a gift card code.']
        throw internalErrors.value
      }

      if (isGiftCard) {
        await accountStore.activateGiftCard(code)
        return { type: CodeInputType.GiftCard }
      } else {
        appliedPromoCode.value = code
        return { type: CodeInputType.PromoCode, code }
      }
    } catch (errors) {
      internalErrors.value = Array.isArray(errors) ? [...errors] : [errors]
      if (!isGiftCardCode(code)) {
        appliedPromoCode.value = null
      }
      throw errors
    } finally {
      promoCodeInput.value = ''
      isApplying.value = false
    }
  }

  const clearPromoCode = () => {
    appliedPromoCode.value = null
  }

  const setPromoCode = (code) => {
    appliedPromoCode.value = code
  }

  return {
    promoCodeInput,
    appliedPromoCode,
    internalErrors,
    isApplying,
    isGiftCardCode,
    applyCode,
    clearPromoCode,
    setPromoCode
  }
}

